import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import EntityDialog from '../../EntityDialog';
import { showToastError, showToastSuccess } from '../../components/ToastNotifications';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import { editFacility, getFacilitiesByOrganization } from '../../hooks/useFacilities';
import { getErrorStatus, throwIfLegacyResultError } from '../../utils/requestErrors';

export default function EditFacilityContainer() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { org } = useLegacyAppSession();
  const facilityId = String(params.facilityId || '');
  const [facility, setFacility] = useState(location.state?.facility || null);
  const [isLoading, setIsLoading] = useState(!location.state?.facility);

  useEffect(() => {
    if (!facilityId || !org?.id || facility) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);

    getFacilitiesByOrganization(org.id, 0, 500)
      .then((response) => {
        if (cancelled) return;
        const facilities = Array.isArray(response?.data) ? response.data : [];
        const match = facilities.find((item) => String(item?.id) === facilityId) || null;
        setFacility(match);
      })
      .catch(() => {
        if (cancelled) return;
        showToastError('Facility could not be loaded.');
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [facility, facilityId, org?.id]);

  if (!facilityId) {
    return <Navigate to="/imports?tab=facilities" replace />;
  }

  if (isLoading) {
    return null;
  }

  if (!facility) {
    return <Navigate to="/imports?tab=facilities" replace />;
  }

  return (
    <EntityDialog
      testId="edit-facility"
      title="Edit Facility"
      contextLabel="Facilities"
      fields={[
        { name: 'name', label: 'Facility Name', required: true },
        { name: 'license', label: 'License', required: true, readOnly: true },
      ]}
      initialValues={{ ...facility }}
      onClose={() => navigate('/imports?tab=facilities', { replace: true })}
      onSave={async (payload) => {
        try {
          const organizationId = facility.organizationId || org?.id;
          if (!organizationId) return;

          const result = await editFacility({
            organizationId,
            facilityId: facility.id,
            name: payload.name,
          });
          throwIfLegacyResultError(result, 'The facility could not be saved. Please try again.');

          showToastSuccess('Facility successfully saved.');
          navigate('/imports?tab=facilities', { replace: true });
        } catch (error) {
          console.error(error);
          if (getErrorStatus(error) !== 502) {
            showToastError('The facility could not be saved. Please try again.');
          }
        }
      }}
    />
  );
}
